import React, { useContext } from 'react';
import algoliasearch from 'algoliasearch';
import cx from 'classnames';
import { InstantSearch, useInstantSearch } from 'react-instantsearch';
import { MultipleQueriesResponse } from '@algolia/client-search';

import DocsContext from 'src/contexts/docs';
import styles from 'src/components/docs/Search/SearchDialog.module.scss';

import SearchInput from './SearchInput';
import SearchResults from './SearchResults';
import { LATEST_API_VERSION } from '../constants';

declare global {
  interface Window {
    aa: (command: string, ...args: any[]) => void;
  }
}

const searchIndex = 'plaid_core_exchange_docs';

// Regex to match versioned reference URLs (e.g., /reference/6.3/)
const VERSION_URL_PATTERN = /\/reference\/(\d+\.\d+)\//;

// Check if a URL should be included in search results
// Returns true for non-versioned URLs or URLs matching the latest version
const shouldIncludeUrl = (url: string): boolean => {
  const match = url.match(VERSION_URL_PATTERN);
  if (!match) return true; // Not a versioned URL, include it
  return match[1] === LATEST_API_VERSION;
};
const searchClient = algoliasearch(
  '862NOJ201C',
  '80c584b60e95780132289b38ef3b4963',
);

const searchClientWrapper = {
  ...searchClient,
  async search<TObject = unknown>(requests) {
    if (requests.every(({ params }) => !params.query)) {
      return Promise.resolve({
        results: requests.map(() => ({
          hits: [],
          nbHits: 0,
          nbPages: 0,
          page: 0,
          processingTimeMS: 0,
          hitsPerPage: 0,
          exhaustiveNbHits: false,
          query: '',
          params: '',
        })),
      }) as Promise<MultipleQueriesResponse<TObject>>;
    }

    const response = await searchClient.search<TObject>(requests);

    // Filter out hits from older API versions (only show latest)
    const filteredResults = response.results.map((result) => {
      if ('hits' in result) {
        const filteredHits = result.hits.filter((hit) => {
          const url = (hit as { url?: string }).url;
          if (!url) return true;
          return shouldIncludeUrl(url);
        });
        return {
          ...result,
          hits: filteredHits,
          nbHits: filteredHits.length,
        };
      }
      return result;
    });

    return { ...response, results: filteredResults };
  },
};

// Component that conditionally renders results only when there's a query
const SearchResultsWrapper: React.FC = () => {
  const { indexUiState, results } = useInstantSearch();
  const hasQuery = Boolean(indexUiState.query);
  const hasResults = results && results.nbHits > 0;

  if (!hasQuery) {
    return null;
  }

  return (
    <div className={styles.resultsWrapper}>
      <div className={styles.resultsContainer}>
        <SearchResults />
      </div>
      {hasResults && <div className={styles.fadeOverlay} />}
    </div>
  );
};

interface SearchDialogProps {
  isVisible: boolean;
}

const SearchDialog: React.FC<SearchDialogProps> = ({ isVisible }) => {
  const { isLoggedIn, user } = useContext(DocsContext);

  React.useEffect(() => {
    let anonId = window.localStorage.getItem('ajs_anonymous_id') ?? '';
    anonId = anonId.replace(/^"(.*)"$/, '$1');
    if (window.aa && isVisible && anonId !== '') {
      window.aa('setUserToken', anonId);
    }
  }, [isVisible]);

  React.useEffect(() => {
    if (isLoggedIn && isVisible && user != null && window.aa) {
      window.aa('setAuthenticatedUserToken', user.userId);
    }
  }, [isLoggedIn, user, isVisible]);

  return (
    <div
      role='dialog'
      className={cx(styles.searchDialog, !isVisible && styles.hidden)}
    >
      {isVisible && (
        <InstantSearch
          searchClient={searchClientWrapper}
          indexName={searchIndex}
          insights={true}
        >
          <SearchInput />
          <SearchResultsWrapper />
        </InstantSearch>
      )}
    </div>
  );
};

SearchDialog.displayName = 'CoreExchangeSearchDialog';
export default SearchDialog;
